import { useCallback, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Overlay } from '../ui/Overlay';
import { formatInZone } from '../lib/datetime';

/**
 * One photograph, and a way to reach the next one — TROOP-116.
 *
 * The gallery opened photographs into the ordinary bottom sheet: a 30rem card at the bottom of the
 * screen, with the caption in the title bar, no attribution beyond a name, and **no way at all to
 * reach the next picture**. Looking through forty photographs from a campout meant closing and
 * re-tapping forty times.
 *
 * So: the whole viewport, arrows, `ArrowLeft`/`ArrowRight`/`Home`/`End`, and a swipe. The counter
 * is what tells you where you are in the roll.
 *
 * **The arrow at either end is hidden, not disabled.** §6 requires a disabled control to state a
 * reason, and "there is no previous photograph" is not a reason anybody can act on — the design
 * system's own answer is that such a control should not render. The counter already says.
 *
 * Motion is a 120ms opacity fade and nothing else, per §8. No slide, no crossfade between images:
 * a photograph that slides in is a photograph somebody is waiting for.
 */
const SWIPE_THRESHOLD = 40;

/**
 * The little a full-screen viewer actually needs to know about the thing it is showing.
 *
 * **`EventPhoto` satisfies this structurally, so the gallery and the feed pass their own rows
 * unchanged.** It exists because TROOP-127 has a second thing worth looking at full-screen — a
 * photographed permission slip — and the alternative was a second lightbox: the same swipe, the
 * same arrows, the same keyboard map, drifting apart the first time one of them was fixed. A slip
 * and a campout photo have nothing in common at the policy layer and everything in common here,
 * which is the split §6 already makes for the bytes themselves.
 */
export interface LightboxItem {
  id: string;
  url: string;
  caption: string | null;
  addedByDisplayName: string;
  capturedAt: string | null;
  canRemove: boolean;
}

export function PhotoLightbox<T extends LightboxItem>({
  photos,
  index,
  zone,
  busy,
  bylinePrefix = 'Added by',
  removeLabel = 'Remove this photo',
  onIndex,
  onClose,
  onRemove,
}: {
  photos: T[];
  index: number;
  /** The event's zone, so a capture time reads as the trip's clock and not the viewer's. */
  zone: string;
  busy: boolean;
  /** "Added by" for a gallery photo, "Entered by" for a returned form. */
  bylinePrefix?: string;
  removeLabel?: string;
  onIndex: (next: number) => void;
  onClose: () => void;
  onRemove: (photo: T) => void;
}) {
  const photo = photos[index];
  const startX = useRef<number | null>(null);

  const go = useCallback(
    (delta: number) => {
      // Clamped rather than wrapped. Wrapping in a two-hundred-photograph gallery lands you at the
      // far end with no sense of having moved.
      const next = Math.min(Math.max(index + delta, 0), photos.length - 1);
      if (next !== index) onIndex(next);
    },
    [index, photos.length, onIndex],
  );

  // Neighbours are decoded ahead, so paging is instant rather than a flash of nothing. Cheap: two
  // images, and the browser has already fetched the thumbnails.
  useEffect(() => {
    for (const offset of [-1, 1]) {
      const neighbour = photos[index + offset];
      if (neighbour) new Image().src = neighbour.url;
    }
  }, [photos, index]);

  // Escape and Tab belong to Overlay; the arrows belong here, because only this screen knows what
  // "next" means.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        onIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        onIndex(photos.length - 1);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [go, onIndex, photos.length]);

  if (!photo) return null;

  return (
    <Overlay
      presentation="lightbox"
      title={`${index + 1} of ${photos.length}`}
      onClose={onClose}
    >
      <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
        <div
          className="flex items-center"
          style={{ gap: 'var(--space-2)' }}
          onPointerDown={(e) => {
            startX.current = e.clientX;
          }}
          onPointerUp={(e) => {
            const from = startX.current;
            startX.current = null;
            if (from === null) return;

            const travelled = e.clientX - from;
            if (Math.abs(travelled) >= SWIPE_THRESHOLD) go(travelled < 0 ? 1 : -1);
          }}
        >
          <Arrow
            direction="previous"
            // Hidden at the end rather than disabled. See the component remarks.
            hidden={index === 0}
            onClick={() => go(-1)}
          />

          <img
            key={photo.id}
            src={photo.url}
            alt={photo.caption ?? `${bylinePrefix} ${photo.addedByDisplayName}`}
            className="flex-1 min-w-0"
            style={{
              display: 'block',
              maxHeight: '70vh',
              objectFit: 'contain',
              // §8: the only motion in the product is a 120ms opacity fade.
              animation: 'th-fade 120ms ease-out',
            }}
          />

          <Arrow
            direction="next"
            hidden={index === photos.length - 1}
            onClick={() => go(1)}
          />
        </div>

        <div className="flex flex-col" style={{ gap: 'var(--space-1)' }}>
          {photo.caption && (
            <p
              className="t-body"
              style={{ color: 'var(--color-text-on-inverse)', margin: 0 }}
            >
              {photo.caption}
            </p>
          )}

          {/* Attribution and the moment it was taken. `capturedAt` was fetched and rendered
              nowhere before this — it is the one fact that says the picture is of the trip rather
              than of the upload, and it reads in the event's zone for the same reason every other
              time on the page does. */}
          <p
            className="t-caption"
            style={{ color: 'var(--color-text-on-inverse)', opacity: 0.8, margin: 0 }}
          >
            {bylinePrefix} {photo.addedByDisplayName}
            {photo.capturedAt && <> &middot; taken {formatInZone(photo.capturedAt, zone)}</>}
          </p>
        </div>

        {photo.canRemove && (
          <div>
            <Button
              variant="destructive"
              onClick={() => onRemove(photo)}
              disabledReason={busy ? 'Working...' : undefined}
            >
              <X size={16} aria-hidden />
              {removeLabel}
            </Button>
          </div>
        )}
      </div>
    </Overlay>
  );
}

function Arrow({
  direction,
  hidden,
  onClick,
}: {
  direction: 'previous' | 'next';
  hidden: boolean;
  onClick: () => void;
}) {
  const Icon = direction === 'previous' ? ChevronLeft : ChevronRight;

  // Kept in the layout when hidden so the photograph does not jump sideways at either end.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'previous' ? 'Previous photo' : 'Next photo'}
      className="inline-flex items-center justify-center shrink-0 rounded-md"
      style={{
        minWidth: 'var(--tap-min)',
        minHeight: 'var(--tap-min)',
        background: 'none',
        border: 'none',
        color: 'var(--color-text-on-inverse)',
        cursor: 'pointer',
        visibility: hidden ? 'hidden' : undefined,
      }}
    >
      <Icon size={24} aria-hidden="true" />
    </button>
  );
}
